import { useCallback, useState } from 'react'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'
import useInnovaAxios from './useInnovaAxios'

function useReportGroups() {
  const [isLoading, setIsLoading] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [isAdding, setIsAdding] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [reportGroups, setReportGroups] = useState([])

  const getReportGroupsRequest = useInnovaAxios({ url: '/reportGroup/getGroups' })
  const deleteReportGroupRequest = useInnovaAxios({ url: '/reportGroup/deleteGroup' })
  const addReportGroupRequest = useInnovaAxios({ url: '/reportGroup/addGroup' })
  const updateReportGroupRequest = useInnovaAxios({ url: '/reportGroup/updateGroup' })
  const testReportGroupRequest = useInnovaAxios({ url: '/reportGroup/sendReport' })

  const timezoneOffset = new Date().getTimezoneOffset()
  /* ============================================================
     Fetch Report Groups
     ============================================================ */

  const fetchReportGroups = useCallback(
    async (organization) => {
      setIsLoading(true)

      try {
        const response = await getReportGroupsRequest({ organization, timezoneOffset })

        if (response?.error) {
          return failure(extractErrorMessage(response.error, 'Failed to fetch alert groups'))
        }

        const reportGroups = Array.isArray(response?.data)
          ? [...response.data].sort((a, b) => (a.groupName ?? '').localeCompare(b.groupName ?? ''))
          : []

        setReportGroups(reportGroups)

        return success('Report groups fetched successfully', reportGroups)
      } finally {
        setIsLoading(false)
      }
    },
    [getReportGroupsRequest, timezoneOffset],
  )

  /* ============================================================
       Update Report Group
       ============================================================ */

  const updateReportGroup = useCallback(
    async (form) => {
      if (!form?.uid) return failure('Invalid report group id')
      if (!form?.groupName) return failure('Invalid group data')
      if (isUpdating) return failure('Update already in progress')

      setIsUpdating(true)
      try {
        const payload = { ...form }

        const res = await updateReportGroupRequest({
          reportGroup: JSON.stringify(payload),
          timezoneOffset,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update report group'))
        }

        return success('Report group updated')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateReportGroupRequest, isUpdating, timezoneOffset],
  )

  /* ============================================================
       Delete Report Group
       ============================================================ */

  const deleteReportGroup = useCallback(
    async (uid) => {
      if (!uid) return failure('Invalid report group id')
      if (isDeleting) return failure('Delete already in progress')

      setIsDeleting(true)
      try {
        const res = await deleteReportGroupRequest({ uid })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Delete failed'))
        }

        return success('Report group deleted')
      } finally {
        setIsDeleting(false)
      }
    },
    [deleteReportGroupRequest, isDeleting],
  )

  /* ============================================================
       Add Report Group
       ============================================================ */

  const addReportGroup = useCallback(
    async (form) => {
      if (isAdding) return failure('Add already in progress')
      form.uid = 0

      setIsAdding(true)
      try {
        const res = await addReportGroupRequest({
          reportGroup: JSON.stringify(form),
          timezoneOffset,
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to create report group'))
        }

        return success('Report group created', res?.data)
      } finally {
        setIsAdding(false)
      }
    },
    [addReportGroupRequest, isAdding, timezoneOffset],
  )

  /* ============================================================
       Test Report Group
       ============================================================ */

  const testReportGroup = useCallback(
    async (reportGroup) => {
      if (isTesting) return failure('Test already in progress')

      setIsTesting(true)
      try {
        const res = await testReportGroupRequest({
          reportGroup: JSON.stringify(reportGroup),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to test report group'))
        }

        return success('Report group tested', res?.data)
      } finally {
        setIsTesting(false)
      }
    },
    [testReportGroupRequest, isTesting],
  )

  return {
    reportGroups,

    isLoading,
    isAdding,
    isDeleting,
    isUpdating,
    isTesting,

    fetchReportGroups,
    addReportGroup,
    updateReportGroup,
    deleteReportGroup,
    testReportGroup,
  }
}

export default useReportGroups
